import React from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Users, 
  Target,
  Award
} from 'lucide-react';

const Benefits: React.FC = () => {
  const benefits = [
    {
      icon: <Clock className="h-12 w-12" />,
      title: "Reduce Detection Time",
      subtitle: "From 28 days to minutes",
      description: "Transform your reactive security model with real-time monitoring and AI-powered threat detection.",
      stats: "95% faster incident detection",
      color: "bg-blue-500"
    },
    {
      icon: <DollarSign className="h-12 w-12" />,
      title: "Lower Security Costs",
      subtitle: "Prevent $4.24M average breach cost",
      description: "Proactive security prevents incidents and reduces the need for expensive security personnel.",
      stats: "60% reduction in security overhead",
      color: "bg-green-500"
    },
    {
      icon: <Users className="h-12 w-12" />,
      title: "Bridge Knowledge Gap",
      subtitle: "AI-powered expertise for all teams",
      description: "Democratize AWS security expertise with intelligent recommendations and automated guidance.",
      stats: "73% of teams lack AWS security skills",
      color: "bg-purple-500"
    },
    {
      icon: <Target className="h-12 w-12" />,
      title: "Ensure Compliance",
      subtitle: "Automated framework adherence",
      description: "Maintain continuous compliance with automated monitoring, reporting, and evidence collection.",
      stats: "8 compliance frameworks supported",
      color: "bg-orange-500"
    }
  ];

  const metrics = [
    { value: "99.9%", label: "Platform Uptime", description: "Enterprise-grade reliability" },
    { value: "<200ms", label: "API Response Time", description: "Lightning-fast performance" },
    { value: "<5min", label: "Data Processing", description: "Real-time security insights" },
    { value: "<24hrs", label: "Onboarding Time", description: "Quick deployment and setup" }
  ];

  return (
    <section id="benefits" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Measurable Security Improvements
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Organizations using AWS Security Guardian see immediate improvements in their 
            security posture, operational efficiency, and compliance readiness.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow card-hover"
            >
              <div className="flex items-start space-x-6">
                <div className={`${benefit.color} text-white p-3 rounded-xl flex-shrink-0`}>
                  {benefit.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-lg font-medium text-gray-600 mb-3">
                    {benefit.subtitle}
                  </p>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {benefit.description}
                  </p>
                  <div className="inline-flex items-center bg-white px-4 py-2 rounded-lg border">
                    <TrendingUp className="h-4 w-4 text-green-500 mr-2" />
                    <span className="text-sm font-medium text-gray-900">{benefit.stats}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Performance Metrics */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 lg:p-12 text-white">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">Platform Performance</h3>
            <p className="text-xl text-blue-100">
              Built for enterprise-scale with AWS-native architecture
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {metrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold mb-2">{metric.value}</div>
                <div className="text-lg font-medium mb-1">{metric.label}</div>
                <div className="text-sm text-blue-100">{metric.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ROI Calculator Preview */}
        <div className="mt-20 bg-gray-50 rounded-2xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Calculate Your Security ROI
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                See how AWS Security Guardian can reduce your security costs while 
                improving your security posture and compliance readiness.
              </p>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center bg-white p-4 rounded-lg">
                  <span className="font-medium">Average Breach Cost Prevented</span>
                  <span className="text-2xl font-bold text-green-600">$4.24M</span>
                </div>
                <div className="flex justify-between items-center bg-white p-4 rounded-lg">
                  <span className="font-medium">Security Personnel Savings</span>
                  <span className="text-2xl font-bold text-green-600">60%</span>
                </div>
                <div className="flex justify-between items-center bg-white p-4 rounded-lg">
                  <span className="font-medium">Compliance Audit Efficiency</span>
                  <span className="text-2xl font-bold text-green-600">80%</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <div className="text-center mb-6">
                <Award className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-900">Security Score Improvement</h4>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Before AWS Security Guardian</span>
                    <span>67%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-red-500 h-3 rounded-full" style={{width: '67%'}}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>After Implementation</span>
                    <span>94%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-green-500 h-3 rounded-full" style={{width: '94%'}}></div>
                  </div>
                </div>
                
                <div className="text-center pt-4">
                  <div className="text-3xl font-bold text-green-600">+27%</div>
                  <div className="text-sm text-gray-600">Average improvement in 30 days</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
